import { useEffect, useState } from 'react'
import DestinationCard from './DestinationCard.jsx'

const TIER_CONFIG = {
  1: {
    title: 'All clear',
    subtitle: 'no forms needed — just book',
    rail: 'border-go-green',
    headingColor: 'text-go-green',
  },
  2: {
    title: 'Some prep',
    subtitle: 'e-visa or eTA for someone in the group',
    rail: 'border-prep-amber',
    headingColor: 'text-prep-amber',
  },
  3: {
    title: 'Visa appointment',
    subtitle: 'possible — each member below needs to apply',
    rail: 'border-visa-coral',
    headingColor: 'text-visa-coral',
  },
}

// Budget bands shown as sub-groups inside each visa tier. Budget never moves a
// destination out of its tier — it only sorts it into one of these bands.
const BUDGET_BANDS = [
  { key: 'within', label: 'Within budget' },
  { key: 'slightly_over', label: 'Slightly over' },
  { key: 'over', label: 'Over budget' },
]

// Collapsible effort tier: header shows the count, tap to expand/collapse.
// The top tier opens by default so the best options are visible immediately.
function CollapsibleTier({ tier, rows, defaultOpen, hasBudget, onRefineOrigin, focus }) {
  // A tier holding a destination the user named opens on its own, otherwise the
  // highlighted card would sit inside a collapsed <details> and never be seen.
  const [open, setOpen] = useState(
    defaultOpen || rows.some((r) => focus.includes(r.jurisdiction.iso2))
  )
  if (!rows.length) return null
  // A budget can be set and the rows still carry no band: with no departure
  // airport anywhere the engine suppresses banding, because a flight-free
  // total cannot honestly be compared to a budget. Grouping by band would then
  // match nothing and DROP EVERY CARD, so fall back to the flat list whenever
  // there is no band to group by.
  const banded = hasBudget && rows.some((r) => r.cost.budget_band)
  const { title, subtitle, rail, headingColor } = TIER_CONFIG[tier]
  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      className={`border-l-[3px] pl-3 ${rail}`}
    >
      <summary className="flex cursor-pointer select-none list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          <span className={`font-display text-base font-semibold ${headingColor}`}>
            {title} ({rows.length})
          </span>
          <span className="block text-xs text-slate-500">{subtitle}</span>
        </span>
        <span className="shrink-0 font-mono text-[10px] text-slate-400">
          {open ? '▲ hide' : '▼ show'}
        </span>
      </summary>
      <div className="mt-3 space-y-4">
        {banded ? (
          BUDGET_BANDS.map(({ key, label }) => {
            const bandRows = rows.filter((r) => r.cost.budget_band === key)
            if (!bandRows.length) return null
            return (
              <div key={key}>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {label} ({bandRows.length})
                </p>
                <ul className="space-y-3">
                  {bandRows.map((row) => (
                    <DestinationCard
                      key={row.jurisdiction.id}
                      row={row}
                      onRefineOrigin={onRefineOrigin}
                      highlight={focus.includes(row.jurisdiction.iso2)}
                    />
                  ))}
                </ul>
              </div>
            )
          })
        ) : (
          /* No budget given: no band sub-groups, one flat severity-sorted list */
          <ul className="space-y-3">
            {rows.map((row) => (
              <DestinationCard
                key={row.jurisdiction.id}
                row={row}
                onRefineOrigin={onRefineOrigin}
                highlight={focus.includes(row.jurisdiction.iso2)}
              />
            ))}
          </ul>
        )}
      </div>
    </details>
  )
}

export default function Results({
  result,
  onEdit,
  onShare,
  shareState,
  onRefineOrigin,
  // ISO2 codes the user named in the search box. Emphasis only: the full
  // group-intersection list is the product, so nothing is ever filtered out.
  focus = []
}) {
  const [showTravelBan, setShowTravelBan] = useState(false)
  const [search, setSearch] = useState('')
  const hasBudget = result.query?.budget_per_person_usd != null

  // Bring the first named destination into view once the cards are laid out.
  // Deferred a frame: the tier holding it opens during this same render.
  const firstFocus = focus[0]
  useEffect(() => {
    if (!firstFocus) return
    const t = setTimeout(() => {
      document
        .getElementById(`dest-${firstFocus}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 60)
    return () => clearTimeout(t)
  }, [firstFocus])

  const tier1 = result.results.filter((r) => r.tier === 1)
  const tier2 = result.results.filter((r) => r.tier === 2)
  const tier3 = result.results.filter((r) => r.tier === 3)
  const travelBan = result.excluded || []

  const totalReachable = tier1.length + tier2.length + tier3.length

  // Country search across ALL groups (incl. travel ban). Each match still
  // renders as its own card, so its tier badge shows where it sits.
  const q = search.trim().toLowerCase()
  const searchMatches = q
    ? [...tier1, ...tier2, ...tier3, ...travelBan].filter(
        (r) =>
          r.jurisdiction.name.toLowerCase().includes(q) ||
          r.jurisdiction.iso2.toLowerCase() === q
      )
    : null

  return (
    <div className="space-y-6">
      {/* Summary + actions */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-800">
            {totalReachable} destinations reachable
          </p>
          <p className="text-xs text-slate-500">
            {tier1.length} all clear · {tier2.length} some prep · {tier3.length} visa appt
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={onEdit}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 active:bg-slate-50"
          >
            Edit
          </button>
          <button
            onClick={onShare}
            className="rounded-lg bg-ocean px-3 py-2 text-xs font-semibold text-white active:opacity-80"
          >
            {shareState === 'copied' ? 'Copied ✓' : 'Share'}
          </button>
        </div>
      </div>

      {/* FIX 4: search bar */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search a country — e.g. Japan"
          className="w-full rounded-lg border border-slate-300 px-3 py-2.5 pr-9 text-base"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-lg text-slate-400"
          >
            ×
          </button>
        )}
      </div>

      {searchMatches ? (
        /* Search results: flat list across all tiers */
        searchMatches.length > 0 ? (
          <section className="space-y-3">
            <p className="text-xs text-slate-500">
              {searchMatches.length} {searchMatches.length === 1 ? 'match' : 'matches'} for
              “{search.trim()}”
            </p>
            <ul className="space-y-3">
              {searchMatches.map((row) => (
                <DestinationCard
                  key={row.jurisdiction.id}
                  row={row}
                  onRefineOrigin={onRefineOrigin}
                  highlight={focus.includes(row.jurisdiction.iso2)}
                />
              ))}
            </ul>
          </section>
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center">
            <p className="text-sm text-slate-500">No destination matches “{search.trim()}”.</p>
          </div>
        )
      ) : (
        /* Grouped, collapsible view */
        <div className="space-y-6">
          {tier1.length > 0 ? (
            <CollapsibleTier tier={1} rows={tier1} defaultOpen hasBudget={hasBudget} onRefineOrigin={onRefineOrigin} focus={focus} />
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center">
              <p className="text-sm text-slate-500">
                No all-clear destinations for this group.
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Check “Some prep” and “Visa appointment” below, or add permits people hold.
              </p>
            </div>
          )}

          <CollapsibleTier tier={2} rows={tier2} defaultOpen={false} hasBudget={hasBudget} onRefineOrigin={onRefineOrigin} focus={focus} />
          <CollapsibleTier tier={3} rows={tier3} defaultOpen={false} hasBudget={hasBudget} onRefineOrigin={onRefineOrigin} focus={focus} />

          {/* Travel ban — the only group outside the visa tiers, collapsed */}
          {travelBan.length > 0 && (
            <details
              className="rounded-xl border border-slate-200 bg-white"
              onToggle={(e) => setShowTravelBan(e.currentTarget.open)}
            >
              <summary className="flex cursor-pointer select-none items-center justify-between px-4 py-3 [&::-webkit-details-marker]:hidden">
                <span className="text-xs font-medium text-slate-500">
                  Travel ban ({travelBan.length})
                </span>
                <span className="font-mono text-[10px] text-slate-400">
                  {showTravelBan ? '▲ hide' : '▼ show'}
                </span>
              </summary>
              <ul className="space-y-3 px-4 pb-4">
                {travelBan.map((row) => (
                  <DestinationCard
                    key={row.jurisdiction.id}
                    row={row}
                    onRefineOrigin={onRefineOrigin}
                    highlight={focus.includes(row.jurisdiction.iso2)}
                  />
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <footer className="rounded-xl bg-white px-4 py-3 text-xs leading-relaxed text-slate-400">
        {result.disclaimer} Visa rules can change.
      </footer>
    </div>
  )
}
